import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2, Eye, EyeOff } from "lucide-react";
import {
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  sendPasswordResetEmail,
  type AuthError,
} from "firebase/auth";
import { firebaseAuth } from "@/lib/firebase";
import { useSession } from "@/lib/auth-client";
import { trackLogin } from "@/lib/analytics";
import { useAuthUIStore } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { LanguageSelector } from "@/components/LanguageSelector";

type Mode = "password" | "magic-link" | "forgot-password";

function getAuthErrorMessage(code: string): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Invalid email or password.";
    case "auth/user-disabled":
      return "This account has been deactivated.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    default:
      return "Sign-in failed. Please try again.";
  }
}

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const { magicLinkSent, magicLinkEmail, setMagicLinkSent } = useAuthUIStore();
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (session) navigate("/dashboard", { replace: true });
  }, [session, navigate]);

  if (isPending) return null;

  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
    setResetSent(false);
  };

  // --- Email + password sign-in ---
  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      trackLogin("password");
    } catch (err) {
      setError(getAuthErrorMessage((err as AuthError).code));
    } finally {
      setLoading(false);
    }
  };

  // --- Forgot password ---
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
      setResetSent(true);
    } catch (err) {
      setError(getAuthErrorMessage((err as AuthError).code));
    } finally {
      setLoading(false);
    }
  };

  // --- Magic link ---
  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await sendSignInLinkToEmail(firebaseAuth, email, {
        url: `${window.location.origin}/auth/v1/verify`,
        handleCodeInApp: true,
      });
      window.localStorage.setItem("emailForSignIn", email);
      setMagicLinkSent(true, email);
    } catch {
      setError("Failed to send sign-in link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-4">
        <div className="flex justify-end">
          <LanguageSelector />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">{import.meta.env.VITE_APP_ENV === "test" ? "Test Arna Med" : "Arna Med"}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Magic link "check inbox" confirmation */}
            {mode === "magic-link" && magicLinkSent ? (
              <div className="text-center space-y-4 py-4">
                <div className="flex justify-center">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <p className="font-medium">Check your inbox</p>
                <p className="text-sm text-muted-foreground">
                  We sent a sign-in link to <strong>{magicLinkEmail}</strong>.
                  <br />
                  It expires in 10&nbsp;minutes.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMagicLinkSent(false)}
                >
                  Use a different email
                </Button>
              </div>
            ) : mode === "password" ? (
              /* Email + password form */
              <form onSubmit={handlePasswordSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="doctor@hospital.kz"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {t("auth.loginButton")}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-muted-foreground hover:underline"
                    onClick={() => switchMode("forgot-password")}
                  >
                    {t("auth.forgotPassword")}
                  </button>
                </div>
              </form>
            ) : mode === "forgot-password" ? (
              /* Forgot password form */
              resetSent ? (
                <div className="text-center space-y-4 py-4">
                  <div className="flex justify-center">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <p className="font-medium">{t("auth.resetSentTitle")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("auth.resetSentBody", { email })}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => switchMode("password")}
                  >
                    {t("auth.backToLogin")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t("auth.forgotPasswordHint")}
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">{t("auth.email")}</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="doctor@hospital.kz"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    {t("auth.sendResetLink")}
                  </Button>
                  <button
                    type="button"
                    className="w-full text-sm text-muted-foreground hover:underline"
                    onClick={() => switchMode("password")}
                  >
                    {t("auth.backToLogin")}
                  </button>
                </form>
              )
            ) : (
              /* Magic link form */
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ml-email">{t("auth.email")}</Label>
                  <Input
                    id="ml-email"
                    type="email"
                    placeholder="doctor@hospital.kz"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Send sign-in link
                </Button>
                <button
                  type="button"
                  className="w-full text-sm text-muted-foreground hover:underline"
                  onClick={() => switchMode("password")}
                >
                  {t("auth.backToLogin")}
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
